import { Dispatch, SetStateAction, useMemo } from "react";
import { ScreeningCriteriaType } from "shared/app/VerificationTemplate/slices/DecisionCriteria/types";
import { Show } from "shared/components";
import {
  ApplicantStatus,
  Application,
  ApplicationStatus,
  DecisionAction,
  ExecutionModes,
  RequiresOverridesIdentityVerification
} from "shared/types";
import { Card } from "stream-chat-react";
import { Applicant, CriteriaCheckSettings, Reasons } from "types/application";
import { RecommendationMethod } from "types/landlord/verificationTemplate";
import { CursorClick01 } from "untitledui-js-base";
import { ApplicationScoreCard } from "views/applications-id/components/ApplicationScoreCard";
import { NoRecommendationCard } from "views/applications-id/components/NoRecommendationCard";
import { RuleBasedRecommendation } from "./components/RuleBasedRecommendation";

const WithWrapper = ({
  children,
  wrapperClassName
}: {
  children: React.ReactNode;
  wrapperClassName?: string;
}) => {
  if (wrapperClassName) {
    return <div className={wrapperClassName}>{children}</div>;
  }
  return <>{children}</>;
};

export const DecisionCard = ({
  requiresOverridesIdentityVerification,
  applicants,

  ruleReasons,

  screeningCriteria,

  autoApproved,

  criteriaCheckSettings,
  applicationStatus,

  canMakeDecision,

  score,
  recommendation,
  recommendationMethod,

  requiresManualOrConditionalExecution,
  hasPendingVerifications,

  setShowDecisionFlowModal,
  setShowExplainRecommendations,

  wrapperClassName,

  magicLinksCount
}: {
  screeningCriteria?: ScreeningCriteriaType;
  criteriaCheckSettings?: CriteriaCheckSettings;
  autoApproved: boolean;
  ruleReasons?: Reasons[];
  applicationStatus: ApplicationStatus;
  applicants: Applicant[];
  requiresOverridesIdentityVerification?: RequiresOverridesIdentityVerification;
  requiresManualOrConditionalExecution?: Application["requires_manual_or_conditional_execution"];
  recommendation?: DecisionAction;
  recommendationMethod?: RecommendationMethod;
  canMakeDecision: boolean;
  score: null | number | undefined;
  hasPendingVerifications: boolean;
  setShowDecisionFlowModal: Dispatch<SetStateAction<boolean>>;
  setShowExplainRecommendations: Dispatch<SetStateAction<boolean>>;

  wrapperClassName?: string;

  magicLinksCount: number;
}) => {
  const totalManualVerifications = useMemo(() => {
    return (
      requiresManualOrConditionalExecution?.applicants?.reduce(
        (count, applicant) => {
          const manualVerificationsCount = applicant.verifications.filter(
            (verification) =>
              verification.execution_mode === ExecutionModes.Manual
          ).length;

          return count + manualVerificationsCount;
        },
        0
      ) || 0
    );
  }, [requiresManualOrConditionalExecution]);

  const scoreBased = recommendationMethod === "score_based",
    ruleBased = recommendationMethod === "rule_based",
    noRecommendationEnabled = !scoreBased && !ruleBased;

  // hide card if application status is not submitted or decision
  // hide if magic links are present
  // hide if any applicant is not submitted
  if (
    [
      ApplicationStatus.PartiallySubmitted,
      ApplicationStatus.OnHold,
      ApplicationStatus.Canceled,
      ApplicationStatus.Started,
      ApplicationStatus.Pending
    ].includes(applicationStatus) ||
    magicLinksCount > 0 ||
    applicants.some(
      (applicant) =>
        ![ApplicantStatus.Submitted, ApplicantStatus.Canceled].includes(
          applicant.application_status
        )
    )
  )
    return null;

  if (totalManualVerifications > 0)
    return (
      <WithWrapper wrapperClassName={wrapperClassName}>
        <Card className="flex flex-col min-w-[300px]">
          <div className="flex flex-grow items-center justify-center bg-primary-20 border-b border-alpha-40 py-[52px]">
            <CursorClick01 className="stroke-primary-500" size="56" />
          </div>

          <div className="p-6 flex flex-col items-center justify-center gap-2">
            <p className="font-bold text-base text-content-1000">
              {totalManualVerifications} reports require manual run
            </p>
            <Show if={scoreBased || ruleBased}>
              <p className="text-content-400 font-medium text-xs text-center">
                Decision will be recommended after all reports have been run
              </p>
            </Show>
          </div>
        </Card>
      </WithWrapper>
    );

  const hasRuleDecisionData =
    ruleBased && (Boolean(recommendation) || (ruleReasons?.length || 0) > 0);
  const hasScoreDecisionData = scoreBased && score != null;

  if (hasPendingVerifications && !hasRuleDecisionData && !hasScoreDecisionData)
    return null;

  if (noRecommendationEnabled)
    return (
      <WithWrapper wrapperClassName={wrapperClassName}>
        <NoRecommendationCard />
      </WithWrapper>
    );

  if (scoreBased && hasScoreDecisionData)
    return (
      <WithWrapper wrapperClassName={wrapperClassName}>
        <ApplicationScoreCard
          applicationScore={score}
          recommendation={recommendation}
          onButtonClick={() => setShowDecisionFlowModal(true)}
          criteriaCheckSettings={criteriaCheckSettings}
          applicationSubmitted={
            applicationStatus === ApplicationStatus.Submitted
          }
          templateCriteriaSettings={screeningCriteria}
          applicants={applicants}
          requires_overrides_identity_verification={
            requiresOverridesIdentityVerification
          }
          status={applicationStatus}
        />
      </WithWrapper>
    );

  if (hasRuleDecisionData)
    return (
      <WithWrapper wrapperClassName={wrapperClassName}>
        <RuleBasedRecommendation
          recommendation={recommendation}
          autoApproved={autoApproved}
          setShowExplainRecommendations={setShowExplainRecommendations}
          setShowDecisionFlowModal={setShowDecisionFlowModal}
          canMakeDecision={canMakeDecision}
          ruleReasons={ruleReasons}
          applicationStatus={applicationStatus}
        />
      </WithWrapper>
    );
};
